import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { Location } from 'react-router-dom'

import { usePostDetailsDialog } from '~publish/components/PostDialogs'
import { readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { buildPostRoute } from '~publish/pages/Community/Comments/CommentsByPost/utils'
import { CommentsPostCard_Post } from '~publish/pages/Community/fragments/CommentsPostCard_Post'
import type { CommentsPostCard_PostData } from '~publish/pages/Community/fragments/CommentsPostCard_Post'
import { useIsCommentsListView } from '~publish/pages/Community/util/routeUtils'

type PostMiniatureActions = {
  post: CommentsPostCard_PostData
  showChannel: boolean
  isListView: boolean
  showAllCommentsRoute: string | null
  handleShowAllComments: () => void
  detailsDialogLocation: Location
}

/**
 * Encapsulates all orchestration logic for PostMiniatureButton:
 * fragment unwrapping, variant selection, route building, and
 * navigation callbacks.
 */
export function usePostMiniatureActions(
  maskedPost: FragmentOf<typeof CommentsPostCard_Post>,
): PostMiniatureActions {
  const post = readFragment(CommentsPostCard_Post, maskedPost)

  // Dialog location for post preview
  const isListView = useIsCommentsListView()
  const { constructDetailsDialogLocation } = usePostDetailsDialog({
    postId: post.id,
    useSingularPath: isListView,
  })

  const detailsDialogLocation = React.useMemo(
    () =>
      constructDetailsDialogLocation({
        cta: 'publish-comments-postMiniature-previewButton-1',
      }),
    [constructDetailsDialogLocation],
  )

  // Channel context
  const { channelId } = useParams<{ channelId?: string }>()
  const showChannel = !channelId
  const isMultiChannel = !channelId

  // "Show all comments" route + navigation
  const showAllCommentsRoute = React.useMemo(
    () =>
      buildPostRoute({
        postId: post.id,
        isMultiChannel,
        channelId: channelId ?? undefined,
      }),
    [post.id, isMultiChannel, channelId],
  )

  const navigate = useNavigate()
  const handleShowAllComments = React.useCallback(() => {
    if (!showAllCommentsRoute) return
    const separator = showAllCommentsRoute.includes('?') ? '&' : '?'
    navigate(`${showAllCommentsRoute}${separator}tab=comments`)
  }, [navigate, showAllCommentsRoute])

  return {
    post,
    showChannel,
    isListView,
    showAllCommentsRoute,
    handleShowAllComments,
    detailsDialogLocation,
  }
}
